import { useCallback } from "react";

import { useDocumentUploadProgress } from "components/content_library/DocumentUploadProgressProvider";
import { CustomerDocumentType } from "lib/types/graphQLEnums";
import { FileWithDirectoryPath } from "./types";
import { useUploadCustomerDocuments } from "./useUploadCustomerDocuments";

type Options = {
  // When true, skips the per-file `startProcessingDocument` step. The caller
  // takes ownership of triggering processing (e.g. the bid orchestrator workflow
  // dispatches it from the backend). Processing status still flows back through
  // the shared `customerDocumentProcessingStatus` subscription, so upload
  // progress is tracked to completion as those events arrive.
  skipProcessing?: boolean;
};

export function useTriggerBidDocumentUpload(
  resourceId: string | undefined,
  { skipProcessing }: Options = {},
) {
  const { startProcessing, updateFileStatus, uploadStatuses } = useDocumentUploadProgress();
  const { mutate: uploadFiles } = useUploadCustomerDocuments(
    updateFileStatus,
    CustomerDocumentType.BidDocument,
  );

  return useCallback(
    (files: FileWithDirectoryPath[], explicitResourceId?: string) => {
      const targetResourceId = explicitResourceId ?? resourceId;
      if (!targetResourceId) return;
      startProcessing(files, targetResourceId);
      uploadFiles({
        files,
        resourceId: targetResourceId,
        operationId: targetResourceId,
        uploadStatuses,
        skipProcessing,
      });
    },
    [resourceId, startProcessing, uploadFiles, uploadStatuses, skipProcessing],
  );
}
